<template>
    <div id="dashboard-producer-report-page" v-if="dashboardDetails">
      <div class="flex-grow mb-4">
        <h1><b>{{ dashboardDetails.HomeTitle }}</b></h1>
      </div>
      <div class="vx-row mb-4 header-cards">
        <div class="vx-col w-full lg:w-1/2">
          <vs-card class="m-0 relative">
            <div slot="header" class="flex items-center">
              <font-awesome-icon class="mr-2 header-card-icon" size="sm" :icon="[dashboardDetails.ProductionDetails.IconType, dashboardDetails.ProductionDetails.Icon]"/>
              <span><b>{{ dashboardDetails.ProductionDetails.Title }}</b></span>
            </div>
            <div class="vx-row">
              <div class="vx-col w-1/3 flex flex-col relative">
                <spinner v-if="!dashboardData"/>
                <div class="header-card-title">{{ dashboardDetails.ProductionDetails.CurrentYearLabel }}</div>
                <div class="header-card-value">{{ productCurrentYearValue }}</div>
              </div>
              <div class="vx-col w-1/3 flex flex-col relative">
                <spinner v-if="!dashboardData"/>
                <div class="header-card-title">{{ dashboardDetails.ProductionDetails.PreviousYearLabel }}</div>
                <div class="header-card-value">{{ productPreviousYearValue }}</div>
              </div>
              <div class="vx-col w-1/3 flex flex-col relative">
                <spinner v-if="!dashboardData"/>
                <div class="header-card-title">{{ dashboardDetails.ProductionDetails.VariationLabel }}</div>
                <div class="header-card-value variation" :style="`color:${productVariationColour}`">
                  <font-awesome-icon v-if="dashboardData" size="xs" :icon="['fal', productVariationIcon]"/>
                  {{ productVariationValue }}
                </div>
              </div>
            </div>
          </vs-card>  
        </div>
        <div class="vx-col w-full lg:w-1/2 pt-4 lg:pt-0">
          <vs-card class="m-0 relative">
            <div slot="header" class="flex items-center">
              <font-awesome-icon class="mr-2 header-card-icon" size="sm" :icon="[dashboardDetails.ContractDetails.IconType, dashboardDetails.ContractDetails.Icon]"/>
              <span><b>{{ dashboardDetails.ContractDetails.Title }}</b></span>
            </div>
            <div class="vx-row">
              <div class="vx-col w-1/3 flex flex-col relative">
                <spinner v-if="!dashboardData"/>
                <div class="header-card-title">{{ dashboardDetails.ContractDetails.CurrentYearLabel }}</div>
                <div class="header-card-value">{{ contractCurrentYearValue }}</div>
              </div>
              <div class="vx-col w-1/3 flex flex-col relative">
                <spinner v-if="!dashboardData"/>
                <div class="header-card-title">{{ dashboardDetails.ContractDetails.PreviousYearLabel }}</div>
                <div class="header-card-value">{{ contractPreviousYearValue }}</div>
              </div>
              <div class="vx-col w-1/3 flex flex-col relative">
                <spinner v-if="!dashboardData"/>
                <div class="header-card-title">{{ dashboardDetails.ContractDetails.VariationLabel }}</div>
                <div class="header-card-value variation" :style="`color:${contractVariationColour}`">
                  <font-awesome-icon v-if="dashboardData" size="xs" :icon="['fal', contractVariationIcon]"/>
                  {{ contractVariationValue }}
                </div>
              </div>
            </div>
          </vs-card>
        </div>
      </div>
      <div class="vx-row charts-row">
        <div class="vx-col w-full xl:w-2/3 main-grid">
          <vs-card class="m-0 relative">
            <spinner v-if="!dashboardData"/>
            <grid-panel
                v-else
                class="dashboard-grid dashboard-main-grid"
                :column-defs="columnDefs"
                :data="dashboardData.GridDetails"
                :loadOnRender="false"
                @gridReady="onGridReady"
                :autoGroupColumnDef="autoGroupColumnDef"
                :showSidebar="false"
                :renderSearch="false"
                :groupDefaultExpanded="-1"
                :defaultColDef="defaultColDef"
                :autoSize="true"
                :gridOptions="gridOptions"
            >
            </grid-panel>
          </vs-card>
        </div>
        <div class="vx-col w-full xl:w-1/3 side-charts">
          <div class="vx-row flex-wrap">
            <div class="vx-col w-full md:w-1/3 xl:w-full bar-chart">
              <vs-card class="m-0 relative">
                <h3 class="p-4">{{ dashboardDetails.ChartList[0].ChartName }}</h3>
                <spinner v-if="!dashboardData"/>
                <vue-apex-charts v-else height="85%" type="bar" :options="barChartOptions" :series="barChartSeries"/>
              </vs-card>
            </div>
            <div class="vx-col w-full md:w-1/3 xl:w-1/2 pie-chart">
              <vs-card class="m-0 relative">
                <h3 class="px-4">{{ dashboardDetails.ChartList[1].ChartName }}</h3>
                <spinner v-if="!dashboardData"/>
                <vue-apex-charts v-else height="100%" type="donut" :options="pieChartCurrentYearOptions" :series="pieSeries.currentYear"/>
              </vs-card>
            </div>
            <div class="vx-col w-full md:w-1/3 xl:w-1/2 pie-chart">
              <vs-card class="m-0 relative">
                <h3 class="px-4">{{ dashboardDetails.ChartList[2].ChartName }}</h3>
                <spinner v-if="!dashboardData"/>
                <vue-apex-charts v-else height="100%" type="donut" :options="pieChartPreviousYearOptions" :series="pieSeries.prevYear"/>
              </vs-card>
            </div>
        </div>
      </div>
      </div>
    </div>
  </template>

  <script>
  import GridPanel from "../../components/ag-grid/GridPanel";
  import DateRangePicker from "../../components/calendar/DateRangePicker";
  import DashboardConfig from "../../../grids/Dashboard/DashboardConfig";
  import PieChartConfig from "../../../grids/DashboardCharts/PieChartConfig";
  import BarChartConfig from "../../../grids/DashboardCharts/BarChartConfig";
  import CellRendererDate from "../../components/ag-grid/cell-renderer/CellRendererDate";
  import CellRendererMasterValue from "../../components/ag-grid/cell-renderer/CellRendererMasterValue";
  import VueApexCharts from 'vue-apexcharts'
  import Spinner from "@/components/Spinner";
  import ToolTipRenderer from '@/views/components/ag-grid/components/ToolTipRenderer';

  export default {
    name: "DashboardGrid",
    components: {
      GridPanel,
      CellRendererDate,
      DateRangePicker,
      CellRendererMasterValue,
      VueApexCharts,
      Spinner,
      ToolTipRenderer,
    },
    data() {
      return {
        languageApiParams: { language: this.$i18n.locale },
        columnDefs: DashboardConfig,
        url: (params) => this.$api.chartInfo.list(params),
        startDate: null,
        endDate: null,
        start: null,
        gridApi: null,
        gridOptions: {
          isExternalFilterPresent: () => true,
          doesExternalFilterPass: (node) => {
            // Always show group rows
            if (node.group) {
              return true;
            }
            // For data rows, only show if Subtitle is not null (filter out summary rows)
            const subtitle = node.data && node.data.Subtitle;
            return subtitle != null && subtitle !== '';
          },
        },
        defaultColDef: {
          sortable: false,
          resizable: false,
          floatingFilter: false,
          suppressMovable: true,
          tooltipComponent: 'ToolTipRenderer',
        },
        autoGroupColumnDef: {
          headerName: '',
          cellRendererParams: { suppressCount: true },
          maxWidth: 60,
          colSpan: (params) => {
            if (!params.data) {
              return 2;
            }

            return 1;
          },
        },
        dashboardData: null,
        dashboardDetails: null,
        pieChartCurrentYearOptions: _.cloneDeep(PieChartConfig),
        pieChartPreviousYearOptions: _.cloneDeep(PieChartConfig),
        barChartOptions: _.cloneDeep(BarChartConfig),
        pieSeries: {
          prevYear: [],
          currentYear: [],
        },
        barChartSeries: [
          {
            name: 'Current Year',
            data: []
          }, {
            name: 'Previous Year',
            data: []
          }
        ],
      };
    },
    async mounted() {
      await this.getDashboardDetails();
      await this.getDashboardData();
    },
    computed: {
      productCurrentYearValue() {
        if (!this.dashboardData) {
          return '';
        }
        
        return this.dashboardData.ProductionDetails.CurrentYearValue
      },
      productPreviousYearValue() {
        if (!this.dashboardData) {
          return '';
        }
        
        return this.dashboardData.ProductionDetails.PreviousYearValue
      },
      productVariationColour() {
        if (!this.dashboardData) {
          return '';
        }
        
        return this.dashboardData.ProductionDetails.VariationColour
      },
      productVariationPositive() {
        if (!this.dashboardData) {
          return '';
        }
        
        return this.dashboardData.ProductionDetails.VariationPositive
      },
      productVariationValue() {
        if (!this.dashboardData) {
          return '';
        }
        
        return this.dashboardData.ProductionDetails.VariationValue
      },
      productVariationIcon() {
        return this.dashboardData.ProductionDetails.VariationPostive ? 'arrow-up' : 'arrow-down';
      },
      contractCurrentYearValue() {
        if (!this.dashboardData) {
          return '';
        }

        return this.dashboardData.ContractDetails.CurrentYearValue;
      },
      contractPreviousYearValue() {
        if (!this.dashboardData) {
          return '';
        }

        return this.dashboardData.ContractDetails.PreviousYearValue;
      },
      contractVariationColour() {
        if (!this.dashboardData) {
          return '';
        }

        return this.dashboardData.ContractDetails.VariationColour;
      },
      contractVariationPositive() {
        if (!this.dashboardData) {
          return '';
        }

        return this.dashboardData.ContractDetails.VariationPositive;
      },
      contractVariationValue() {
        if (!this.dashboardData) {
          return '';
        }

        return this.dashboardData.ContractDetails.VariationValue;
      },
      contractVariationIcon() {
        return this.dashboardData.ContractDetails.VariationPostive ? 'arrow-up' : 'arrow-down';
      },
    },
    methods: {
      onGridReady($params) {
        // Store grid API reference
        this.gridApi = $params.api;
      },
      async getDashboardData() {
        try {
          const { data } = await this.$api.dashboardData.list({params: this.languageApiParams});
          this.dashboardData = data.data;
          
          this.dashboardData.ChartDetails.forEach((chartData) => {
            this.pieSeries.currentYear.push(chartData.CurrentYearAmt);
            this.pieSeries.prevYear.push(chartData.PrevYearAmt);

            const classOfBusinessDetails = this.dashboardDetails.ClassOfBusinessList.find((classOfBusiness) =>
              classOfBusiness.RiskCodeReference_PK === chartData.RiskCodeReference_PK);

            const currentYearData = {
              x: '',
              y: chartData.CurrentYearAmt,
              fillColor: '',
            };

            const previousYearData = {
              x: '',
              y: chartData.PrevYearAmt,
              fillColor: '',
            };

            if (!this.pieChartCurrentYearOptions.labels.includes(classOfBusinessDetails.Code)) {
              this.pieChartCurrentYearOptions.labels.push(classOfBusinessDetails.Code);
              this.pieChartCurrentYearOptions.colors.push(classOfBusinessDetails.ColourCurrentYear);
            }

            if (!this.pieChartPreviousYearOptions.labels.includes(classOfBusinessDetails.Code)) {
              this.pieChartPreviousYearOptions.labels.push(classOfBusinessDetails.Code);
              this.pieChartPreviousYearOptions.colors.push(classOfBusinessDetails.ColourPreviousYear);
            }

            if (!this.barChartOptions.xaxis.categories.includes(classOfBusinessDetails.Code)) {
              this.barChartOptions.xaxis.categories.push(classOfBusinessDetails.Code);
              currentYearData.fillColor = classOfBusinessDetails.ColourCurrentYear;
              previousYearData.fillColor = classOfBusinessDetails.ColourPreviousYear;
            }
            
            // Current Year
            this.barChartSeries[0].data.push(currentYearData);
            
            // Previous Year
            this.barChartSeries[1].data.push(previousYearData);
          })
        } catch (e) {
          this.$vs.notify({
            title: this.$t('ErrorOccurred'),
            text: this.$t('DocumentTypesFetchFailureMessage'),
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger',
            position: 'top-right',
          });
        }
      },
      async getDashboardDetails() {
        try {
          const { data } = await this.$api.dashboardDetails.list({params: this.languageApiParams});
          this.dashboardDetails = data.data;
        } catch (e) {
          this.$vs.notify({
            title: this.$t('ErrorOccurred'),
            text: this.$t('DocumentTypesFetchFailureMessage'),
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger',
            position: 'top-right',
          });
        }
      },
    },
  }
  </script>
